import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiShoppingBag, FiArrowLeft, FiPlus, FiMinus, FiX } from 'react-icons/fi';
import Buy3Banner from '../../components/Buy3Banner/Buy3Banner';
import { CONFIG } from '../../config/config';
import { useCart } from '../../contexts/CartContext';
import { hasEarlyAccess, useEarlyAccessListener } from '../../utils/earlyAccess';
import {
  computeBuy3Get1DiscountInr,
  computePayableTotalInr,
  computeTotalSavingsVsOriginalInr,
  formatInr,
  getPricePresentation,
} from '../../utils/pricing';
import './CartPage.css';

const CartPage = () => {
  useEarlyAccessListener();
  const navigate = useNavigate();
  const { cart, updateQuantity, removeItem } = useCart();
  const items = cart.items || [];
  const payable = computePayableTotalInr(items);
  const promoOff = computeBuy3Get1DiscountInr(items);
  const savedVsOriginal = computeTotalSavingsVsOriginalInr(items);
  const tax = Math.round(payable * 0.08);
  const total = payable + tax;
  const showBestDeal = hasEarlyAccess();
  const price = getPricePresentation();

  const EmptyCart = () => (
    <div className="empty-cart">
      <div className="empty-cart-icon">
        <FiShoppingBag />
      </div>
      <h2>Your cart is empty</h2>
      <p className="empty-cart-description">
        Browse T-Shirts and tap {CONFIG.CTA.BUY_NOW} to add your first piece.
      </p>
      <div className="empty-cart-actions">
        <Link to="/products?category=t-shirts" className="btn btn-primary">
          <FiArrowLeft />
          Continue shopping
        </Link>
      </div>
    </div>
  );

  return (
    <div className="cart-page">
      <Buy3Banner />

      <div className="cart-header">
        <div className="container">
          <button type="button" className="back-btn" onClick={() => navigate(-1)}>
            <FiArrowLeft />
          </button>
          <h1>Cart</h1>
          <p className="cart-subtitle">
            {items.length === 0 ? 'No items yet' : `${items.length} line items`}
          </p>
          {items.length > 0 && (
            <div className="cart-header-total">
              Total: <span className="cart-header-amount">{formatInr(total)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="container">
        {items.length === 0 ? (
          <EmptyCart />
        ) : (
          <div className="cart-content">
            <div className="cart-items-list">
              {items.map((item) => (
                <div key={item._id} className="cart-item">
                  <div className="item-product">
                    <div className="product-image">
                      <img src={item.image || '/images/tee-1.svg'} alt={item.name} />
                    </div>
                    <div className="product-details">
                      <h3 className="product-name">{item.name}</h3>
                      <p className="product-variant">
                        {item.color} / {item.size}
                      </p>
                      {showBestDeal && (
                        <span className="cart-best-deal-badge">Best Deal</span>
                      )}
                    </div>
                  </div>
                  <span className="item-price">
                    <s className="item-price-original">{formatInr(price.originalInr)}</s>{' '}
                    {formatInr(item.unitPriceInr)}
                  </span>
                  <div className="item-quantity">
                    <div className="quantity-selector">
                      <button
                        type="button"
                        className="quantity-btn"
                        onClick={() =>
                          updateQuantity(item._id, Math.max(1, item.quantity - 1))
                        }
                      >
                        <FiMinus size={14} />
                      </button>
                      <span className="quantity">{item.quantity}</span>
                      <button
                        type="button"
                        className="quantity-btn"
                        onClick={() => updateQuantity(item._id, item.quantity + 1)}
                      >
                        <FiPlus size={14} />
                      </button>
                    </div>
                  </div>
                  <span className="item-total">
                    {formatInr(item.unitPriceInr * item.quantity)}
                  </span>
                  <div className="item-actions">
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeItem(item._id)}
                      aria-label="Remove"
                    >
                      <FiX size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="cart-summary">
              <h3>Order summary</h3>
              {CONFIG.SALE.ENABLE_BUY3_GET1 && promoOff > 0 && (
                <div className="summary-row summary-row--promo">
                  <span>Buy 3 Get 1</span>
                  <span>−{formatInr(promoOff)}</span>
                </div>
              )}
              <div className="summary-row">
                <span>Subtotal</span>
                <span>{formatInr(payable)}</span>
              </div>
              <div className="summary-row">
                <span>Tax (8%)</span>
                <span>{formatInr(tax)}</span>
              </div>
              {savedVsOriginal > 0 && (
                <p className="cart-saved-line">You saved {formatInr(savedVsOriginal)}</p>
              )}
              <div className="summary-row total">
                <span>Total</span>
                <span>{formatInr(total)}</span>
              </div>
              <Link
                to="/checkout"
                className="btn btn-primary checkout-btn"
              >
                Checkout
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
